const normalizeOptionValue = (value?: string) =>
	(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim()
		.toLowerCase();

const mapSingleValueToCanonical = (
	value: string | undefined,
	canonicalValues: string[],
	esValues: string[],
	enUiValues: string[],
	ptValues: string[]
) => {
	if (!value) {
		return value;
	}

	const normalizedValue = normalizeOptionValue(value);
	for (let index = 0; index < canonicalValues.length; index++) {
		const candidates = [
			canonicalValues[index],
			esValues[index],
			enUiValues[index],
			ptValues[index],
		];

		if (candidates.some((candidate) => normalizeOptionValue(candidate) === normalizedValue)) {
			return canonicalValues[index];
		}
	}

	return value;
};

const mapDelimitedValueToCanonical = (
	value: string | undefined,
	canonicalValues: string[],
	esValues: string[],
	enUiValues: string[],
	ptValues: string[]
) => {
	if (!value) {
		return value;
	}

	const mappedValues = value
		.split(";")
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => mapSingleValueToCanonical(part, canonicalValues, esValues, enUiValues, ptValues));

	return mappedValues.join(";");
};

type OptionLanguage = "es" | "en" | "pt";

const normalizeOptionLanguage = (language?: string): OptionLanguage => {
	const normalizedLanguage = (language || "").trim().toLowerCase();

	if (normalizedLanguage.startsWith("en")) {
		return "en";
	}

	if (normalizedLanguage.startsWith("pt")) {
		return "pt";
	}

	return "es";
};

const getOptionsByLanguage = (
	language: string | undefined,
	esValues: string[],
	enValues: string[],
	ptValues: string[]
) => {
	switch (normalizeOptionLanguage(language)) {
		case "en":
			return enValues;
		case "pt":
			return ptValues;
		default:
			return esValues;
	}
};

export const SECTOR_CANONICAL_OPTIONS = [
	"Construction",
	"International Trade",
	"Professional Athlete",
	"Antique Dealer",
	"Precious Metal Trading",
	"Non-profit Organizations",
	"Foreign Currency Trading",
	"Auditing Company",
	"Public Administrations",
	"Tobacco Industry",
	"Automobiles",
	"Jewelry",
	"Wholesale",
	"Real Estate Management and Rental",
	"E-commerce",
	"Financial Intermediaries",
	"Art Trading",
	"Casinos",
	"Law Firm",
	"Notary's office",
];

const SECTOR_ES_OPTIONS = [
	"Construcción",
	"Comercio Internacional",
	"Deportista profesional",
	"Anticuario",
	"Comercio de metales preciosos",
	"Organismos sin ánimo de lucro",
	"Compra-venta moneda extranjera",
	"Empresa de Auditoría",
	"Administraciones Públicas",
	"Industria tabaquera",
	"Automóviles",
	"Joyería",
	"Venta al por mayor",
	"Gestión y alquiler de bienes inmuebles",
	"Comercio electrónico",
	"Intermediarios financieros",
	"Comercio de arte",
	"Casinos",
	"Despacho de Abogado",
	"Notaría",
];

const SECTOR_EN_UI_OPTIONS = [
	"Construction",
	"International trade",
	"Professional athlete",
	"Antique dealer",
	"Precious metal trade",
	"Non-profit organizations",
	"Foreign currency trading",
	"Audit firm",
	"Public Administrations",
	"Tobacco Industry",
	"Automobiles",
	"Jewelry",
	"Wholesale",
	"Real Estate Management and Rental",
	"E-commerce",
	"Financial Intermediaries",
	"Art Trading",
	"Casinos",
	"Law Firm",
	"Notary's office",
];

const SECTOR_PT_OPTIONS = [
	"Construção",
	"Comércio internacional",
	"Desportista profissional",
	"Antiquário",
	"Comércio de metais preciosos",
	"Organismos sem fins lucrativos",
	"Compra e venda de moeda estrangeira",
	"Empresa de Auditoria",
	"Administrações Públicas",
	"Indústria do tabaco",
	"Automóveis",
	"Joalharia",
	"Venda por grosso",
	"Gestão e aluguer de bens imóveis",
	"Comércio eletrónico",
	"Intermediários financeiros",
	"Comércio de arte",
	"Cassinos",
	"Escritório de advocacia",
	"Cartório notarial",
];

export const PHYSICAL_SOURCE_CANONICAL_OPTIONS = [
	"Salary",
	"Business activity",
	"Sale of goods",
	"Compensation",
	"Donation",
	"Inheritance",
	"Financial investments",
];

const PHYSICAL_SOURCE_ES_OPTIONS = [
	"Salario",
	"Actividad empresarial",
	"Venta de bienes",
	"Indemnización",
	"Donación",
	"Herencia",
	"Inversiones financieras",
];

const PHYSICAL_SOURCE_EN_UI_OPTIONS = [
	"Salary",
	"Business activity",
	"Sale of goods",
	"Compensation",
	"Donation",
	"Inheritance",
	"Financial investments",
];

const PHYSICAL_SOURCE_PT_OPTIONS = [
	"Salário",
	"Atividade empresarial",
	"Venda de bens",
	"Indenização",
	"Doação",
	"Herança",
	"Investimentos financeiros",
];

export const LEGAL_SOURCE_CANONICAL_OPTIONS = [
	"Business activity",
	"Real estate income",
	"Financial income",
];

const LEGAL_SOURCE_ES_OPTIONS = [
	"Actividad empresarial",
	"Rentas inmobiliarias",
	"Rentas financieras",
];

const LEGAL_SOURCE_EN_UI_OPTIONS = [
	"Business activity",
	"Real estate income",
	"Financial income",
];

const LEGAL_SOURCE_PT_OPTIONS = [
	"Atividade empresarial",
	"Rendimentos imobiliários",
	"Rendimentos financeiros",
];

export const AMOUNT_CANONICAL_OPTIONS = [
	"Up to 30,000 euros",
	"From 30,000 to 60,000 euros",
	"From 60,000 to 100,000 euros",
	"From 100,000 to 300,000 euros",
	"More than 300,000",
];

const AMOUNT_ES_OPTIONS = [
	"Hasta 30.000 euros",
	"De 30.000 a 60.000 euros",
	"De 60.000 a 100.000 euros",
	"De 100.000 a 300.000 euros",
	"Más de 300.000",
];

const AMOUNT_EN_UI_OPTIONS = [
	"Up to 30,000 euros",
	"From 30,000 to 60,000 euros",
	"From 60,000 to 100,000 euros",
	"From 100,000 to 300,000 euros",
	"More than 300,000",
];

const AMOUNT_PT_OPTIONS = [
	"Até 30.000 euros",
	"De 30.000 a 60.000 euros",
	"De 60.000 a 100.000 euros",
	"De 100.000 a 300.000 euros",
	"Mais de 300.000",
];

export const INCOME_CANONICAL_OPTIONS = [
	"No annual income",
	...AMOUNT_CANONICAL_OPTIONS,
];

const INCOME_ES_OPTIONS = [
	"Sin ingresos anuales",
	...AMOUNT_ES_OPTIONS,
];

const INCOME_EN_UI_OPTIONS = [
	"No annual income",
	...AMOUNT_EN_UI_OPTIONS,
];

const INCOME_PT_OPTIONS = [
	"Sem rendimentos anuais",
	...AMOUNT_PT_OPTIONS,
];

export const ACTIVITY_CANONICAL_OPTIONS = [
	"Self-employed",
	"Employee",
	"Retired",
	"Unemployed",
	"Pensioner",
	"Student",
];

const ACTIVITY_ES_OPTIONS = [
	"Cuenta propia",
	"Cuenta ajena",
	"Jubilado",
	"Desempleado",
	"Pensionista",
	"Estudiante",
];

const ACTIVITY_EN_UI_OPTIONS = [
	"Self-employed",
	"Employee",
	"Retired",
	"Unemployed",
	"Pensioner",
	"Student",
];

const ACTIVITY_PT_OPTIONS = [
	"Conta própria",
	"Empregado",
	"Reformado",
	"Desempregado",
	"Pensionista",
	"Estudante",
];

export const JOB_TITLE_CANONICAL_OPTIONS = [
	"Director / Partner",
	"Senior Technician",
	"Technician",
	"Administrative",
	"Not applicable",
];

const JOB_TITLE_ES_OPTIONS = [
	"Director / socio",
	"Técnico Senior",
	"Técnico",
	"Administrativo",
	"No aplicable",
];

const JOB_TITLE_EN_UI_OPTIONS = [
	"Director / Partner",
	"Senior Technician",
	"Technician",
	"Administrative",
	"Not applicable",
];

const JOB_TITLE_PT_OPTIONS = [
	"Diretor / Sócio",
	"Técnico Senior",
	"Técnico",
	"Administrativo",
	"Não aplicável",
];

export const getSectorOptions = (language?: string) =>
	getOptionsByLanguage(language, SECTOR_ES_OPTIONS, SECTOR_EN_UI_OPTIONS, SECTOR_PT_OPTIONS);

export const getPhysicalSourceOptions = (language?: string) =>
	getOptionsByLanguage(language, PHYSICAL_SOURCE_ES_OPTIONS, PHYSICAL_SOURCE_EN_UI_OPTIONS, PHYSICAL_SOURCE_PT_OPTIONS);

export const getLegalSourceOptions = (language?: string) =>
	getOptionsByLanguage(language, LEGAL_SOURCE_ES_OPTIONS, LEGAL_SOURCE_EN_UI_OPTIONS, LEGAL_SOURCE_PT_OPTIONS);

export const getAmountOptions = (language?: string) =>
	getOptionsByLanguage(language, AMOUNT_ES_OPTIONS, AMOUNT_EN_UI_OPTIONS, AMOUNT_PT_OPTIONS);

export const getIncomeOptions = (language?: string) =>
	getOptionsByLanguage(language, INCOME_ES_OPTIONS, INCOME_EN_UI_OPTIONS, INCOME_PT_OPTIONS);

export const getActivityOptions = (language?: string) =>
	getOptionsByLanguage(language, ACTIVITY_ES_OPTIONS, ACTIVITY_EN_UI_OPTIONS, ACTIVITY_PT_OPTIONS);

export const getJobTitleOptions = (language?: string) =>
	getOptionsByLanguage(language, JOB_TITLE_ES_OPTIONS, JOB_TITLE_EN_UI_OPTIONS, JOB_TITLE_PT_OPTIONS);

export const toEnglishSectorValue = (value?: string) =>
	mapDelimitedValueToCanonical(value, SECTOR_CANONICAL_OPTIONS, SECTOR_ES_OPTIONS, SECTOR_EN_UI_OPTIONS, SECTOR_PT_OPTIONS);

export const toEnglishPhysicalSourceValue = (value?: string) =>
	mapDelimitedValueToCanonical(value, PHYSICAL_SOURCE_CANONICAL_OPTIONS, PHYSICAL_SOURCE_ES_OPTIONS, PHYSICAL_SOURCE_EN_UI_OPTIONS, PHYSICAL_SOURCE_PT_OPTIONS);

export const toEnglishLegalSourceValue = (value?: string) =>
	mapDelimitedValueToCanonical(value, LEGAL_SOURCE_CANONICAL_OPTIONS, LEGAL_SOURCE_ES_OPTIONS, LEGAL_SOURCE_EN_UI_OPTIONS, LEGAL_SOURCE_PT_OPTIONS);

export const getLocalizedPhysicalSourceValue = (value: string | undefined, language?: string) => {
	if (!value) {
		return value || "";
	}

	const canonicalValue = mapSingleValueToCanonical(
		value,
		PHYSICAL_SOURCE_CANONICAL_OPTIONS,
		PHYSICAL_SOURCE_ES_OPTIONS,
		PHYSICAL_SOURCE_EN_UI_OPTIONS,
		PHYSICAL_SOURCE_PT_OPTIONS,
	);

	const optionIndex = PHYSICAL_SOURCE_CANONICAL_OPTIONS.findIndex(
		(option) => normalizeOptionValue(option) === normalizeOptionValue(canonicalValue),
	);

	if (optionIndex < 0) {
		return value;
	}

	return getPhysicalSourceOptions(language)[optionIndex] || value;
};

export const getLocalizedLegalSourceValue = (value: string | undefined, language?: string) => {
	if (!value) {
		return value || "";
	}

	const canonicalValue = mapSingleValueToCanonical(
		value,
		LEGAL_SOURCE_CANONICAL_OPTIONS,
		LEGAL_SOURCE_ES_OPTIONS,
		LEGAL_SOURCE_EN_UI_OPTIONS,
		LEGAL_SOURCE_PT_OPTIONS,
	);

	const optionIndex = LEGAL_SOURCE_CANONICAL_OPTIONS.findIndex(
		(option) => normalizeOptionValue(option) === normalizeOptionValue(canonicalValue),
	);

	if (optionIndex < 0) {
		return value;
	}

	return getLegalSourceOptions(language)[optionIndex] || value;
};

export const getFundSourceDocumentTranslationKey = (value?: string) => {
	const normalizedValue = normalizeOptionValue(value);

	switch (normalizedValue) {
		case "salary":
			return "fundsOriginSalaryDocuments";
		case "business activity":
			return "fundsOriginBusinessActivityDocuments";
		case "sale of goods":
			return "fundsOriginSaleOfGoodsDocuments";
		case "compensation":
			return "fundsOriginCompensationDocuments";
		case "donation":
			return "fundsOriginDonationDocuments";
		case "inheritance":
			return "fundsOriginInheritanceDocuments";
		case "financial investments":
			return "fundsOriginFinancialInvestmentsDocuments";
		case "real estate income":
			return "fundsOriginRealEstateIncomeDocuments";
		case "financial income":
			return "fundsOriginFinancialIncomeDocuments";
		default:
			return undefined;
	}
};

export const getFundSourceDocumentOptions = (translations: any, value?: string, isLegalInvestor = false) => {
	const normalizedValue = normalizeOptionValue(value);
	if (isLegalInvestor && normalizedValue === "business activity") {
		const legalDocumentOptions = translations?.fundsOriginLegalBusinessActivityDocuments || [];
		const alternativeDocument = translations?.actividadInversor;
		return Array.isArray(legalDocumentOptions)
			? [...legalDocumentOptions, alternativeDocument].filter(Boolean)
			: alternativeDocument ? [alternativeDocument] : [];
	}

	const translationKey = getFundSourceDocumentTranslationKey(value);
	const documentOptions = translationKey ? translations?.[translationKey] || [] : [];

	return Array.isArray(documentOptions) ? documentOptions : [];
};

export const toEnglishAmountValue = (value?: string) =>
	mapSingleValueToCanonical(value, AMOUNT_CANONICAL_OPTIONS, AMOUNT_ES_OPTIONS, AMOUNT_EN_UI_OPTIONS, AMOUNT_PT_OPTIONS);

export const toEnglishIncomeValue = (value?: string) =>
	mapSingleValueToCanonical(value, INCOME_CANONICAL_OPTIONS, INCOME_ES_OPTIONS, INCOME_EN_UI_OPTIONS, INCOME_PT_OPTIONS);

export const toEnglishActivityValue = (value?: string) =>
	mapSingleValueToCanonical(value, ACTIVITY_CANONICAL_OPTIONS, ACTIVITY_ES_OPTIONS, ACTIVITY_EN_UI_OPTIONS, ACTIVITY_PT_OPTIONS);

export const toEnglishJobTitleValue = (value?: string) =>
	mapSingleValueToCanonical(value, JOB_TITLE_CANONICAL_OPTIONS, JOB_TITLE_ES_OPTIONS, JOB_TITLE_EN_UI_OPTIONS, JOB_TITLE_PT_OPTIONS);
